import useHasPermissionUser from "@/composables/useHasPermissionUser";
import { Chain } from "@/internal/modules/providers/interfaces/Chain";
import { useQueryClient } from "@tanstack/vue-query";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { log } from "node:console";
import { computed, reactive, ref } from "vue";
import { useToast } from "vue-toastification";
import useChains from "./useChains";
import useSearchChains from "./useSearchChains";

const useChainForm = (chain?: Chain) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const isCoincidence = ref(false);
  const isConfirmed = ref(false);

  interface Form {
    id: number;
    name: string;
    logo?: File | string | null;
  }

  const form = reactive<Form>({
    id: chain?.id ?? 0,
    name: chain?.name ?? "",
    logo: null,
  });

  const modalAction = ref(false);

  const actionModal = () => {
    modalAction.value = !modalAction.value;
  };

  const { hasPermissionUser } = useHasPermissionUser();

  const modalActionEdit = ref(false);

  const actionEdit = () => {
    modalActionEdit.value = !modalActionEdit.value;
  };

  const isModifying = ref(false);

  const {
    data: chains,
    setQuery,
    refetch,
    isLoading,
  } = useSearchChains({
    immediate: false,
    queryKey: "search-Chains-coincidences",
  });

  const duplicateBrands = computed(() => {
    return chains.value?.data.filter(
      (chain) =>
        chain.name.toLocaleLowerCase() === form.name!.toLocaleLowerCase(),
    );
  });

  const formRules = {
    name: {
      required: helpers.withMessage("El nombre es requerido", required),
    },
  };

  const {
    createChainAsync,
    createChainLoading,
    createChainMediaAsync,
    createChainMediaLoading,
    updateChainAsync,
    updateChainLoading,
  } = useChains({
    immediate: false,
  });

  const formVuelidate = useVuelidate(formRules, form);

  const handleEvaluation = async () => {
    for (const field of formVuelidate.value.$silentErrors) {
      if (!Array.isArray(field.$message)) {
        toast.error(field.$message);
        return;
      }
    }

    isConfirmed.value = true;
    setQuery(form.name!.toLocaleLowerCase());
    await refetch();

    if (!duplicateBrands.value?.length) {
      if (chains.value?.data?.length) {
        isCoincidence.value = true;
      } else {
        await handleCreate();
      }
    } else {
      isConfirmed.value = false;
      toast.error(`Esta cadena con este nombre ya existe ${form.name}`);
      return;
    }
  };

  const handleCreate = async () => {
    try {
      isConfirmed.value = true;

      const data = await createChainAsync({
        name: form.name!.toLowerCase(),
      });

      if (form.logo) {
        await createChainMediaAsync({
          id: data.id,
          collection: "logo",
          media: form.logo as File,
        });
      }

      toast.success(`Cadena creada con éxito`);

      actionModal();
      clearState();

      await queryClient.invalidateQueries(["chains", "list-chains"]);
      await queryClient.invalidateQueries([
        "chains",
        "search-Chains-coincidences",
      ]);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  const handleEvaluationEdit = async () => {
    for (const field of formVuelidate.value.$silentErrors) {
      if (!Array.isArray(field.$message)) {
        toast.error(field.$message);
        return;
      }
    }

    if (hasPermissionUser("update-internal", "App\\Models\\Chain")) {
      isConfirmed.value = true;

      setQuery(form.name!.toLocaleLowerCase());
      await refetch();

      if (chains.value?.data.length) {
        isCoincidence.value = true;
      } else {
        await handleEdit();
      }
    } else {
      const data = await createChainMediaAsync({
        id: form.id,
        collection: "logo",
        media: form.logo as File,
      });

      isModifying.value = false;

      toast.success(`Cadena actualizada con éxito`);

      await queryClient.invalidateQueries(["chain"]);
    }
  };

  const handleEdit = async () => {
    try {
      isConfirmed.value = true;

      const data = await updateChainAsync({
        id: form.id!,
        name: form.name!.toLowerCase(),
      });

      if (isModifying.value) {
        await createChainMediaAsync({
          id: form.id,
          collection: "logo",
          media: form.logo as File,
        });
      }

      if (data) {
        actionEdit();
        isCoincidence.value = false;
        isConfirmed.value = false;
        isModifying.value = false;

        await queryClient.invalidateQueries(["chain"]);
        await queryClient.invalidateQueries(["chains", "list-chains"]);
        await queryClient.invalidateQueries([
          "chains",
          "search-Chains-coincidences",
        ]);
      }
    } catch (error: any) {
      console.log(error);
      toast.error(`Error al crear la cadena`);
    }
  };

  const clearState = async () => {
    form.name = "";
    isCoincidence.value = false;
    isConfirmed.value = false;
    form.id = 0;
    isModifying.value = false;
    form.logo = null;
  };

  return {
    form,
    duplicateBrands,
    chains,
    isCoincidence,
    isConfirmed,
    modalAction,
    handleEvaluation,
    handleCreate,
    clearState,
    actionModal,
    isLoading,
    createChainLoading,

    handleEvaluationEdit,
    handleEdit,
    actionEdit,
    modalActionEdit,
    updateChainLoading,
    isModifying,
    createChainMediaLoading,
  };
};

export default useChainForm;
